import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Send, Eye, MousePointer, AlertTriangle, CheckCircle2,
  XCircle, ArrowLeft, RefreshCw, BarChart3, Mail
} from 'lucide-react';
import { EmailPreviewModal } from './EmailPreviewModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Mailgun webhooks were configured on May 28, 2026 — campaigns before this
// only have accurate "Sent" counts. Delivered / Opened / Clicked are unknown.
const TRACKING_ENABLED_SINCE = new Date(
  process.env.REACT_APP_TRACKING_ENABLED_SINCE || '2026-05-28T00:00:00Z'
);
const isUntracked = (campaign) => {
  if (!campaign?.created_at) return false;
  return new Date(campaign.created_at) < TRACKING_ENABLED_SINCE;
};

export const EmailAnalyticsTab = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [previewCampaignId, setPreviewCampaignId] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [campRes, statsRes] = await Promise.all([
        axios.get(`${API}/email/campaigns`),
        axios.get(`${API}/email/stats`),
      ]);
      setCampaigns(campRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error('Failed to load email analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchCampaignDetail = async (campaignId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/email/campaigns/${campaignId}`);
      setCampaignDetail(res.data);
      setSelectedCampaign(campaignId);
    } catch (e) {
      toast.error('Failed to load campaign details');
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  // Campaign detail view
  if (selectedCampaign && campaignDetail) {
    const camp = campaignDetail.campaign;
    const logs = campaignDetail.logs;
    const untracked = isUntracked(camp);
    return (
      <div className="space-y-6">
        <button onClick={() => { setSelectedCampaign(null); setCampaignDetail(null); }} className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 font-medium" data-testid="back-to-campaigns">
          <ArrowLeft className="w-4 h-4" /> Back to Campaigns
        </button>

        {/* Campaign Header */}
        <div className="bg-white border border-zinc-200 p-6">
          <div className="flex justify-between items-start mb-4 gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-black text-zinc-900">{camp.subject}</h2>
              <p className="text-sm text-zinc-500 mt-1">
                {new Date(camp.created_at).toLocaleString()} &bull; {camp.email_type?.replace('_',' ')}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <button
                onClick={() => setPreviewCampaignId(camp.id)}
                data-testid="analytics-view-email-btn"
                className="flex items-center gap-1.5 bg-zinc-900 hover:bg-red-600 text-white px-3 py-1.5 text-[11px] font-black uppercase tracking-wider transition-colors"
              >
                <Eye className="w-3 h-3" />
                View sent email
              </button>
              <span className={`px-3 py-1 text-xs font-bold uppercase ${camp.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                {camp.status}
              </span>
              {untracked && (
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-500 border border-zinc-200">
                  Untracked
                </span>
              )}
            </div>
          </div>

          {untracked && (
            <div className="mb-4 bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-900 leading-relaxed">
              <strong>Note:</strong> Delivery tracking was enabled on{' '}
              {TRACKING_ENABLED_SINCE.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
              This campaign was sent before that date, so only the &quot;Sent&quot; count is accurate. Delivered / Opened / Clicked data was never received from Mailgun for these messages.
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <StatBox label="Sent" value={camp.sent} icon={Send} color="text-blue-600" bg="bg-blue-50" />
            <StatBox label="Failed" value={camp.failed} icon={XCircle} color="text-red-600" bg="bg-red-50" />
            <StatBox label="Delivered" value={camp.delivered} icon={CheckCircle2} color="text-green-600" bg="bg-green-50" rate={camp.delivery_rate} dim={untracked} />
            <StatBox label="Opened" value={camp.opened} icon={Eye} color="text-purple-600" bg="bg-purple-50" rate={camp.open_rate} dim={untracked} />
            <StatBox label="Clicked" value={camp.clicked} icon={MousePointer} color="text-indigo-600" bg="bg-indigo-50" rate={camp.click_rate} dim={untracked} />
            <StatBox label="Bounced" value={camp.bounced} icon={AlertTriangle} color="text-amber-600" bg="bg-amber-50" rate={camp.bounce_rate} dim={untracked} />
            <StatBox label="Complained" value={camp.complained} icon={XCircle} color="text-red-600" bg="bg-red-50" dim={untracked} />
          </div>
        </div>

        {/* Individual Email Logs */}
        <div className="bg-white border border-zinc-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100">
            <span className="text-sm font-bold text-zinc-700">{logs.length} Recipients</span>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full" data-testid="campaign-logs-table">
              <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-zinc-500">Delivered</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-zinc-500">Opened</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-zinc-500">Clicked</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-zinc-500">Opens</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Sent At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-2.5 text-sm font-medium text-zinc-900">{log.carrier_email}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase ${
                        log.status === 'sent' ? 'bg-green-100 text-green-700' :
                        log.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-zinc-100 text-zinc-600'
                      }`}>{log.status}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center">{log.delivered ? <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" /> : <span className="text-zinc-300">-</span>}</td>
                    <td className="px-4 py-2.5 text-center">{log.opened ? <Eye className="w-4 h-4 text-purple-500 mx-auto" /> : <span className="text-zinc-300">-</span>}</td>
                    <td className="px-4 py-2.5 text-center">{log.clicked ? <MousePointer className="w-4 h-4 text-indigo-500 mx-auto" /> : <span className="text-zinc-300">-</span>}</td>
                    <td className="px-4 py-2.5 text-center text-sm text-zinc-600">{log.open_count || 0}</td>
                    <td className="px-4 py-2.5 text-sm text-zinc-500">{log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {previewCampaignId && (
          <EmailPreviewModal campaignId={previewCampaignId} onClose={() => setPreviewCampaignId(null)} />
        )}
      </div>
    );
  }

  // Campaign list view
  return (
    <div className="space-y-6">
      {previewCampaignId && (
        <EmailPreviewModal campaignId={previewCampaignId} onClose={() => setPreviewCampaignId(null)} />
      )}
      {/* Overall Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white border border-zinc-200 p-4 text-center">
            <div className="text-2xl font-black text-blue-600">{stats.total_sent}</div>
            <div className="text-xs font-medium text-zinc-500 uppercase">Total Sent</div>
          </div>
          <div className="bg-white border border-zinc-200 p-4 text-center">
            <div className="text-2xl font-black text-green-600">{stats.total_delivered}</div>
            <div className="text-xs font-medium text-zinc-500 uppercase">Delivered</div>
          </div>
          <div className="bg-white border border-zinc-200 p-4 text-center">
            <div className="text-2xl font-black text-purple-600">{stats.total_opened}</div>
            <div className="text-xs font-medium text-zinc-500 uppercase">Opened ({stats.open_rate}%)</div>
          </div>
          <div className="bg-white border border-zinc-200 p-4 text-center">
            <div className="text-2xl font-black text-indigo-600">{stats.total_clicked}</div>
            <div className="text-xs font-medium text-zinc-500 uppercase">Clicked ({stats.click_rate}%)</div>
          </div>
          <div className="bg-white border border-zinc-200 p-4 text-center">
            <div className="text-sm font-bold text-zinc-700">{stats.sent_today} / {stats.daily_limit}</div>
            <div className="text-xs font-medium text-zinc-500 uppercase">Sent Today</div>
            <div className="mt-1 h-1.5 bg-zinc-100 overflow-hidden">
              <div className="h-full bg-blue-500" style={{ width: `${Math.min(100, (stats.sent_today / stats.daily_limit) * 100)}%` }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign List */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold text-zinc-900">Email Campaigns</h2>
        <button onClick={fetchData} className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 text-sm" data-testid="refresh-campaigns">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div className="text-center py-20 bg-white border border-zinc-200">
          <BarChart3 className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-zinc-700 mb-2">No campaigns yet</h3>
          <p className="text-zinc-500">Email analytics will appear here after you send notifications.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(camp => {
            const untracked = isUntracked(camp);
            return (
            <div
              key={camp.id}
              className="w-full bg-white border border-zinc-200 p-5 hover:border-zinc-300 transition-colors relative group"
              data-testid={`campaign-${camp.id}`}
            >
              <button
                onClick={(e) => { e.stopPropagation(); setPreviewCampaignId(camp.id); }}
                data-testid={`campaign-preview-${camp.id}`}
                title="View sent email"
                className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 focus:opacity-100 flex items-center gap-1 bg-zinc-900 hover:bg-red-600 text-white px-2 py-1 text-[10px] font-black uppercase tracking-wider transition-all"
              >
                <Eye className="w-3 h-3" />
                View email
              </button>
              <button
                onClick={() => fetchCampaignDetail(camp.id)}
                className="w-full text-left"
                data-testid={`campaign-open-${camp.id}`}
              >
              <div className="flex justify-between items-start mb-3 gap-3 pr-24">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-zinc-900 truncate">{camp.subject}</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {new Date(camp.created_at).toLocaleString()} &bull; {camp.email_type?.replace('_', ' ')}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`px-2 py-0.5 text-xs font-bold uppercase ${
                    camp.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>{camp.status}</span>
                  {untracked && (
                    <span
                      className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-500 border border-zinc-200"
                      title="Sent before delivery tracking was enabled. Only the 'Sent' count is accurate."
                      data-testid={`campaign-untracked-${camp.id}`}
                    >
                      Untracked
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                <MiniStat label="Sent" value={camp.sent} />
                <MiniStat label="Delivered" value={camp.delivered} rate={camp.delivery_rate} dim={untracked} />
                <MiniStat label="Opened" value={camp.opened} rate={camp.open_rate} dim={untracked} />
                <MiniStat label="Clicked" value={camp.clicked} rate={camp.click_rate} dim={untracked} />
                <MiniStat label="Bounced" value={camp.bounced} rate={camp.bounce_rate} warn dim={untracked} />
                <MiniStat label="Failed" value={camp.failed} warn />
              </div>
              {untracked && (
                <p className="mt-3 text-[11px] text-zinc-500 italic">
                  Delivery tracking was enabled on {TRACKING_ENABLED_SINCE.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. Earlier sends were not tracked by Mailgun webhooks.
                </p>
              )}
            </button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const StatBox = ({ label, value, icon: Icon, color, bg, rate, dim }) => (
  <div className={`${dim ? 'bg-zinc-50' : bg} p-4 text-center border border-zinc-100`}>
    <Icon className={`w-5 h-5 ${dim ? 'text-zinc-300' : color} mx-auto mb-1`} />
    <div className={`text-xl font-black ${dim ? 'text-zinc-400' : color}`}>
      {dim ? '—' : (value || 0)}
    </div>
    <div className="text-xs text-zinc-500 uppercase font-bold">{label}</div>
    {!dim && rate !== undefined && <div className="text-xs text-zinc-400 mt-0.5">{rate}%</div>}
  </div>
);


const MiniStat = ({ label, value, rate, warn, dim }) => (
  <div className="text-center">
    <div className={`text-lg font-bold ${dim ? 'text-zinc-400' : warn && value > 0 ? 'text-red-600' : 'text-zinc-900'}`}>
      {dim ? '—' : (value || 0)}
    </div>
    <div className={`text-xs ${dim ? 'text-zinc-400' : 'text-zinc-500'}`}>
      {label}{!dim && rate !== undefined ? ` (${rate}%)` : ''}
    </div>
  </div>
);
